import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { TrendingDown, TrendingUp, Minus, Activity } from "lucide-react";
import Button from "./common/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "./common/Dialog";
import Spinner from "./common/Spinner";
import { fetchPatientHealthHistory } from "../apis/api.patientHealthHistory";
import { queryPatientHealthHistoryKey } from "../apis/queryKeys";
import { formatDateTimeDMY } from "../lib/ScheduleUtils";
import useBrandTranslation from "../hooks/useBrandTranslation";

const YES_NO = { yes: "Sí", no: "No" };

function formatNumber(value, suffix = "") {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return `${num}${suffix}`;
}

function calcBmi(weightKg, heightCm) {
  const weight = Number(weightKg);
  const height = Number(heightCm);
  if (!Number.isFinite(weight) || !Number.isFinite(height) || height <= 0) {
    return null;
  }
  const meters = height / 100;
  return weight / (meters * meters);
}

function DeltaBadge({ current, previous, suffix, invertColors = false, noChangeLabel = "sin cambio" }) {
  if (current == null || previous == null) return null;
  const diff = Number(current) - Number(previous);
  if (!Number.isFinite(diff) || diff === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
        <Minus className="h-3 w-3" />
        {noChangeLabel}
      </span>
    );
  }

  const isDown = diff < 0;
  const positiveTone = invertColors ? isDown : !isDown;
  const Icon = isDown ? TrendingDown : TrendingUp;
  const tone = positiveTone ? "text-emerald-700 bg-emerald-50" : "text-amber-700 bg-amber-50";

  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>
      <Icon className="h-3 w-3" />
      {diff > 0 ? "+" : ""}
      {diff.toFixed(1)}
      {suffix}
    </span>
  );
}

function MetricCard({ label, value, previousValue, suffix, invertColors, noChangeLabel }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
      {previousValue != null && (
        <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
          <span>Antes: {previousValue}</span>
          <DeltaBadge
            current={parseFloat(String(value))}
            previous={parseFloat(String(previousValue))}
            suffix={suffix}
            invertColors={invertColors}
            noChangeLabel={noChangeLabel}
          />
        </div>
      )}
    </div>
  );
}

export default function PatientHealthHistoryDialog({
  patientId,
  patientName,
  triggerLabel,
  triggerClassName,
  triggerVariant = "outline",
  triggerSize = "xs",
}) {
  const { t } = useBrandTranslation();

  const { data: snapshots = [], isLoading, isError } = useQuery({
    queryKey: queryPatientHealthHistoryKey(patientId),
    queryFn: () => fetchPatientHealthHistory(patientId),
    enabled: !!patientId,
  });

  const summary = useMemo(() => {
    if (!snapshots.length) return null;

    const chronological = [...snapshots].sort(
      (a, b) => new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime()
    );
    const latest = snapshots[0];
    const earliest = chronological[0];
    const previous = snapshots[1] || null;
    const bmi = calcBmi(latest.weight_kg, latest.height_cm);

    return { latest, earliest, previous, bmi, chronological };
  }, [snapshots]);

  const title = patientName
    ? t("app.healthHistory.titleOf", "Evolución de {{name}}", { name: patientName })
    : t("app.healthHistory.title", "Evolución de datos de salud");
  const noChangeLabel = t("app.healthHistory.noChange", "sin cambio");

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant={triggerVariant}
          size={triggerSize}
          className={triggerClassName}
          onClick={(e) => e.stopPropagation()}
        >
          <Activity className="h-4 w-4" />
          <span>{triggerLabel || t("app.healthHistory.viewEvolution", "Ver evolución")}</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[95vw] max-w-3xl max-h-[85vh] overflow-y-auto rounded-xl p-6">
        <div className="mb-4 flex items-start justify-between gap-3">
          <DialogTitle className="pr-2 text-lg font-bold leading-snug text-gray-900">
            {title}
          </DialogTitle>
          <DialogClose asChild>
            <button
              type="button"
              className="shrink-0 rounded-full p-1 transition-colors hover:bg-gray-100"
              aria-label={t("common.close", "Cerrar")}
            >
              ×
            </button>
          </DialogClose>
        </div>

        {isLoading && (
          <div className="py-10">
            <Spinner />
          </div>
        )}

        {isError && (
          <p className="text-sm text-red-600">
            {t("app.healthHistory.loadError", "No se pudo cargar el historial de salud.")}
          </p>
        )}

        {!isLoading && !isError && snapshots.length === 0 && (
          <p className="text-sm text-gray-500">
            {t(
              "app.healthHistory.empty",
              "Todavía no hay registros históricos. Se guardan automáticamente cuando el paciente actualiza sus datos de salud."
            )}
          </p>
        )}

        {!isLoading && !isError && summary && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <MetricCard
                label={t("app.profile.weight", "Peso")}
                value={formatNumber(summary.latest.weight_kg, " kg")}
                previousValue={
                  summary.previous?.weight_kg != null
                    ? formatNumber(summary.previous.weight_kg, " kg")
                    : summary.earliest?.weight_kg != null &&
                        summary.earliest.id !== summary.latest.id
                      ? formatNumber(summary.earliest.weight_kg, " kg")
                      : null
                }
                suffix=" kg"
                invertColors
                noChangeLabel={noChangeLabel}
              />
              <MetricCard
                label={t("app.profile.height", "Altura")}
                value={formatNumber(summary.latest.height_cm, " cm")}
                previousValue={
                  summary.previous?.height_cm != null
                    ? formatNumber(summary.previous.height_cm, " cm")
                    : null
                }
                suffix=" cm"
                noChangeLabel={noChangeLabel}
              />
              <MetricCard
                label={t("app.healthHistory.bmi", "IMC")}
                value={summary.bmi != null ? summary.bmi.toFixed(1) : "—"}
                previousValue={null}
              />
            </div>

            {summary.earliest.weight_kg != null &&
              summary.latest.weight_kg != null &&
              summary.earliest.id !== summary.latest.id && (
                <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                  {t("app.healthHistory.weightEvolution", "Evolución de peso:")}{" "}
                  <strong>{formatNumber(summary.earliest.weight_kg, " kg")}</strong>
                  {" → "}
                  <strong>{formatNumber(summary.latest.weight_kg, " kg")}</strong>
                </div>
              )}

            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      {t("app.healthHistory.date", "Fecha")}
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      {t("app.profile.weight", "Peso")}
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      {t("app.profile.height", "Altura")}
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      {t("app.healthHistory.habits", "Hábitos")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {snapshots.map((row, index) => {
                    const previousRow = snapshots[index + 1];
                    return (
                      <tr key={row.id}>
                        <td className="px-3 py-3 whitespace-nowrap text-gray-700">
                          {formatDateTimeDMY(row.recorded_at)}
                        </td>
                        <td className="px-3 py-3 text-gray-900">
                          <div className="flex items-center gap-2">
                            <span>{formatNumber(row.weight_kg, " kg")}</span>
                            <DeltaBadge
                              current={row.weight_kg}
                              previous={previousRow?.weight_kg}
                              suffix=" kg"
                              invertColors
                              noChangeLabel={noChangeLabel}
                            />
                          </div>
                        </td>
                        <td className="px-3 py-3 text-gray-900">
                          {formatNumber(row.height_cm, " cm")}
                        </td>
                        <td className="px-3 py-3 text-gray-700">
                          <div className="space-y-0.5">
                            <p>
                              {t("app.profile.smoking", "Tabaquismo")}:{" "}
                              {YES_NO[row.smoking] || "—"}
                            </p>
                            <p>
                              {t("app.profile.sedentary", "Sedentarismo")}:{" "}
                              {YES_NO[row.sedentary] || "—"}
                            </p>
                            <p>
                              {t("app.profile.toxics", "Tóxicos")}:{" "}
                              {YES_NO[row.toxics] || "—"}
                            </p>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
